// The free plan (OpenSpec 123). One place for its limits, its public state and
// the wording that names it, so every page says the same thing.
//
// The server owns the numbers. The constants below are the documented defaults
// (decisions D1 to D9). They are used while `GET /api/public/free-plan` loads
// and when it cannot be read, and they match what the endpoint returns today.

import { MONITOR_LOCATIONS } from '@/lib/check-locations'

/** `open`: signups work. `full`: the account cap is reached. `closed`: the plan is switched off. */
export type FreePlanState = 'open' | 'full' | 'closed'

/** `GET /api/public/free-plan`, as the web app uses it. */
export type PublicFreePlan = {
  state: FreePlanState
  /** Monitors one free organization may have. */
  monitors: number
  /** Seconds between two checks of one monitor. */
  intervalSeconds: number
  /** City of the one free location, without the "(free)" suffix. */
  location: string
}

/** Region code of the free location. The probe region the server dispatches free checks to. */
export const FREE_REGION_CODE = 'eu-free'

/** The documented free plan limits. Pro-only items are listed in `FREE_PLAN_EXCLUDES`. */
export const FREE_PLAN = {
  monitors: 5,
  intervalSeconds: 30,
  location: 'Nuremberg',
  /** Monitor types a free organization can create (D4). */
  monitorTypes: ['http', 'keyword'],
  /** Chat channels next to email: Discord, Slack or Telegram (D5). */
  chatChannels: 1,
  statusPages: 1,
  teamMembers: 1,
  /**
   * Days of history the dashboard shows. The server clamps the history window of a free
   * (or unknown) plan to 30 days. The step of the data depends on the window, for every plan.
   */
  historyDays: 30,
} as const

/** What the free plan does not include (D4, D5), in the words used on the pricing page. */
export const FREE_PLAN_EXCLUDES =
  'ping, port, DNS, SSL, domain, heartbeat and browser checks, webhook, SMS and phone-call alerts, API keys, email subscribers on the status page, and more than one location'

/** What Pro adds, in one phrase: the upgrade hint (D15). Numbers match `tiers` in `plans.ts`. */
export const PRO_ADDS = MONITOR_LOCATIONS.plan.proAdds

/** Shown while the state loads and when it cannot be read: open, with the documented limits. */
export const FREE_PLAN_FALLBACK: PublicFreePlan = {
  state: 'open',
  monitors: FREE_PLAN.monitors,
  intervalSeconds: FREE_PLAN.intervalSeconds,
  location: FREE_PLAN.location,
}

const STATES: readonly FreePlanState[] = ['open', 'full', 'closed']

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0

/**
 * Read the endpoint body (`{ state, monitors, interval_seconds, location }`, or the same
 * inside `{ data }`). A body that cannot be read is an error, and an error is treated as
 * `open`: a missing answer must never hide the plan or show it as full.
 */
export function parsePublicFreePlan(raw: unknown): PublicFreePlan {
  const body = isRecord(raw) && isRecord(raw.data) ? raw.data : raw
  if (!isRecord(body) || !STATES.includes(body.state as FreePlanState)) return FREE_PLAN_FALLBACK
  return {
    state: body.state as FreePlanState,
    monitors: isCount(body.monitors) ? body.monitors : FREE_PLAN_FALLBACK.monitors,
    intervalSeconds: isCount(body.interval_seconds) ? body.interval_seconds : FREE_PLAN_FALLBACK.intervalSeconds,
    location: typeof body.location === 'string' && body.location.trim() ? body.location.trim() : FREE_PLAN_FALLBACK.location,
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** "Nuremberg (free)": how the free location is named wherever regions are shown. */
export function freeLocationLabel(location: string = FREE_PLAN.location): string {
  return `${location} (free)`
}

/** What to do when the server says `email_not_confirmed`: how to confirm the address. */
export const CONFIRM_EMAIL_HINT = 'Sign in with an email code or link, Google or GitHub to confirm it.'

/** Whether a monitor type is allowed on the free plan. */
export function isFreeMonitorType(type: string): boolean {
  return (FREE_PLAN.monitorTypes as readonly string[]).includes(type)
}
